import { useId, type KeyboardEvent } from 'react';
import { cx } from '@platform/core/lib/cx';
import type { ITabsProps } from './ITabsProps';
import { rovingKey } from './rovingKey';

/**
 * Смуга вкладок (UIUX/controls/tabs, forms/tabs): 38px, проміжок 20px, вибрана — 2px лінія акценту знизу й текст ink, лічильник —
 * пігулка mono 11px після підпису (показується й нуль, як у прототипі); не вміщається — прокручується вбік без перенесення.
 * Клавіатура (tablist): у порядку Tab — лише активна вкладка, ← → і Home/End перемикають вкладки. Кожна вкладка має id; з `idBase` активна
 * вкладка вказує aria-controls на панель, яку споживач позначає tabPanelProps(idBase, active).
 */
export function Tabs({ tabs, active, onChange, idBase }: ITabsProps) {
  const auto = useId();
  const base = idBase ?? auto;
  const move = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const next = rovingKey(e.key, index, tabs.length);
    if (next === null || e.key === 'ArrowUp' || e.key === 'ArrowDown') return;
    e.preventDefault();
    onChange(tabs[next]!.key);
    (e.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
  };
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t, i) => (
        <button key={t.key} id={`${base}-tab-${t.key}`} type="button" role="tab" aria-selected={t.key === active}
          aria-controls={idBase && t.key === active ? `${idBase}-panel` : undefined}
          tabIndex={t.key === active || (!tabs.some((x) => x.key === active) && i === 0) ? 0 : -1}
          className={cx('tabs__tab', t.key === active && 'is-active')} onClick={() => onChange(t.key)} onKeyDown={(e) => move(e, i)}>
          {t.label}
          {t.count !== undefined && <span className="tabs__count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}
